// cSpell:ignore Bedrag choosable deze Gebouw geen kosten pand's periode Provisies Scenariolaan unticked voor zijn
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  Button,
  Card,
  DataTable,
  Lozenge,
  OptionListShared,
  Stack,
  Icon,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import styled from "styled-components";
import {
  buildingAddressOf,
  buildingSectionTitle,
  costSections,
} from "../../../../buildingSettlement.utils";
import { distributionLozenge } from "../../../../components/CostsTable";
import { DepartureShareStatus } from "../../../../components/DepartureShareStatus";
import { LoadFailedBanner } from "../../../../components/ListLoadFailed";
import { Money, MoneyWithVat, VatLine } from "../../../../components/Money";
import { leftForYearlyReadings } from "../../../../departure.utils";
import { unitAddressLabel, unitBoxLabel } from "../../../../groupUnits.utils";
import {
  CATEGORY_LABELS_NL,
  Invoice,
  settlementEligible,
  settlementIneligibleReason,
} from "../../../../types";
import {
  chosenCosts,
  getCostsPastPeriod,
  getLateCosts,
  getNoCostsText,
  getOpenCostsWarning,
  getUnitInvoicesIntro,
  splitSettledCosts,
  yearsWithoutCommonCost,
} from "../settlementWizard.utils";
import { INVOICE_SELECT_COLUMNS } from "./invoiceSelectColumns";
import { CELL_PADDING } from "./provisionColumns";
import {
  btwOf,
  CollapsibleCard,
  fmtDate,
  Hint,
  overlapsPeriod,
  StepHeader,
  withinPeriod,
} from "./shared";
import { StepContext } from "./types";

// The intro spans the card's full width (Lewis, 2026-10-17), as every
// step's does.
const IntroCopy = styled.div`
  margin-bottom: 16px;
`;

// A departure settlement's costs that wait for the readings of the year and
// that the broker left out (see leftForYearlyReadings): not proposed, but the
// broker may still take one, which then no longer follows in the yearly one.
const awaitsReadingsIn = (ctx: StepContext) => (invoice: Invoice) =>
  !!ctx.departureContractId &&
  !ctx.settledInvoiceIds.has(invoice.id) &&
  leftForYearlyReadings({ invoice, draft: ctx.draft });

// Bedrag cell: excl-btw amount + the btw amount on a subdued sub-line.
const amountCell = (inv: Invoice): React.ReactNode => (
  <MoneyWithVat amount={inv.amountExclVat} vat={btwOf(inv)} />
);

// The checkbox column DataTable puts before the columns.
const SELECT_COLUMN_WIDTH = 40;

const SelectTableLayout = styled.div`
  table {
    table-layout: fixed;
  }
  /* A word or a date is never split over two lines ("16/10/202 6"). */
  th,
  td,
  th *,
  td * {
    word-break: normal;
    overflow-wrap: normal;
  }
  th:first-child,
  td:first-child {
    width: ${SELECT_COLUMN_WIDTH}px;
  }
  ${INVOICE_SELECT_COLUMNS.map(
    (column, index) => `
  th:nth-child(${index + 2}),
  td:nth-child(${index + 2}) {
    width: ${column.width};
  }`,
  ).join("")}
`;

const InvoiceSelectTable: React.FC<{
  invoices: Invoice[];
  ctx: StepContext;
}> = ({ invoices, ctx }) => {
  const { draft, saveDraft, settledInvoiceIds } = ctx;

  // An already settled cost is not in the table (Lewis, 2026-10-17): it
  // can't be chosen, so it waits folded away under the table instead, and
  // only when it belongs to this period (M24).
  const isSettled = (i: Invoice) =>
    !(ctx.groupOnlyInvoiceIds?.has(i.id) ?? false) &&
    settledInvoiceIds.has(i.id);
  const { choosable, settledInPeriod: settledCosts } = splitSettledCosts({
    invoices,
    isSettled,
    period: draft.period,
  });
  if (choosable.length === 0 && settledCosts.length === 0) {
    return (
      <TextStyle variation="subdued">
        {getLocalizedText("settlement.invoices.none_in_period")}
      </TextStyle>
    );
  }

  // Choosing a cost undoes "Er zijn geen kosten voor deze periode".
  const setMany = (changes: Record<string, boolean>) =>
    saveDraft({
      selectedInvoiceIds: { ...draft.selectedInvoiceIds, ...changes },
      ...(draft.noCostsConfirmed && Object.values(changes).some(Boolean)
        ? { noCostsConfirmed: false }
        : {}),
    });

  const selectedIds = choosable
    .filter(i => draft.selectedInvoiceIds[i.id])
    .map(i => i.id);
  // A late cost of a period already settled (decision 11).
  const lateIds = new Set(lateCostsOf(ctx, invoices).invoices.map(i => i.id));
  const awaitsReadings = awaitsReadingsIn(ctx);

  return (
    <>
      {choosable.length === 0 ? (
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.invoices.all_settled")}
        </TextStyle>
      ) : (
        <SelectTableLayout>
          <DataTable
            multiSelect
            selectedIds={selectedIds}
            columnContentTypes={[
              "text",
              "text",
              "text",
              "text",
              "text",
              "numeric",
            ]}
            headings={INVOICE_SELECT_COLUMNS.map(column =>
              getLocalizedText(column.heading),
            )}
            onSelectRow={id => setMany({ [String(id)]: true })}
            onDeselectRow={id => setMany({ [String(id)]: false })}
            onSelectPage={() =>
              setMany(Object.fromEntries(choosable.map(i => [i.id, true])))
            }
            onDeselectPage={() =>
              setMany(Object.fromEntries(choosable.map(i => [i.id, false])))
            }
            rows={choosable.map(i => {
              const inPeriod = overlapsPeriod(i, draft.period);
              const groupOnly = ctx.groupOnlyInvoiceIds?.has(i.id) ?? false;
              return {
                id: i.id,
                multiSelectDisabled: groupOnly,
                multiSelectDisabledTooltip: groupOnly
                  ? getLocalizedText("settlement.invoices.group_cost_tooltip")
                  : undefined,
                content: [
                  <div>
                    <TextStyle variation="strong">
                      {CATEGORY_LABELS_NL[i.category]}
                    </TextStyle>
                    <div>
                      <TextStyle variation="subdued" size="small">
                        {i.supplier || "—"}
                      </TextStyle>
                    </div>
                  </div>,
                  i.description || "—",
                  fmtDate(i.date),
                  <Stack spacing="tight" alignment="center">
                    <TextStyle variation="subdued" size="small">
                      {fmtDate(i.periodFrom)} → {fmtDate(i.periodTo)}
                    </TextStyle>
                    {!inPeriod && (
                      <Lozenge appearance="warning">
                        {getLocalizedText(
                          lateIds.has(i.id)
                            ? "settlement.invoices.late_cost"
                            : "settlement.invoices.outside_period",
                        )}
                      </Lozenge>
                    )}
                    {/* Runs past the period (decision 1): not ticked by
                        default, the banner under the table says why. */}
                    {inPeriod &&
                      !ctx.departureContractId &&
                      !withinPeriod(i, draft.period) && (
                        <Lozenge appearance="warning">
                          {getLocalizedText(
                            "settlement.invoices.partly_in_period",
                          )}
                        </Lozenge>
                      )}
                    {(ctx.departureSharesByCost?.[i.id] ?? []).length > 0 && (
                      <div>
                        <DepartureShareStatus
                          shares={ctx.departureSharesByCost?.[i.id] ?? []}
                        />
                      </div>
                    )}
                    {awaitsReadings(i) && (
                      <Lozenge appearance="default">
                        {getLocalizedText(
                          "settlement.departure.yearly_readings_label",
                        )}
                      </Lozenge>
                    )}
                  </Stack>,
                  distributionLozenge(i),
                  amountCell(i),
                ],
              };
            })}
          />
        </SelectTableLayout>
      )}
      <SettledCosts invoices={settledCosts} ctx={ctx} />
    </>
  );
};

const SettledToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 12px;
  padding: 4px 0;
  border: 0;
  background: none;
  cursor: pointer;
  font: inherit;
  color: ${({ theme }) => theme.getColor("gray", 600)};
`;

// The costs of the period an earlier confirmed settlement already took:
// folded away under the table, each with where it went.
const SettledCosts: React.FC<{ invoices: Invoice[]; ctx: StepContext }> = ({
  invoices,
  ctx,
}) => {
  const [open, setOpen] = React.useState(false);
  if (invoices.length === 0) return null;
  return (
    <div>
      <SettledToggle type="button" onClick={() => setOpen(o => !o)}>
        <Icon source={open ? "chevronDown" : "chevronRight"} size="small" />
        {getLocalizedText("settlement.invoices.settled_section", {
          count: String(invoices.length),
        })}
      </SettledToggle>
      {/* The same columns and widths as the table above (Lewis,
          2026-10-17), greyed, with an empty first column where the
          checkboxes stand; the section's title says they're settled. */}
      {open && (
        <SelectTableLayout>
          <DataTable
            columnContentTypes={[
              "text",
              "text",
              "text",
              "text",
              "text",
              "text",
              "numeric",
            ]}
            headings={[
              "",
              ...INVOICE_SELECT_COLUMNS.map(column =>
                getLocalizedText(column.heading),
              ),
            ]}
            rows={invoices.map(i => ({
              id: i.id,
              content: [
                "",
                <div>
                  <TextStyle variation="subdued">
                    {CATEGORY_LABELS_NL[i.category]}
                  </TextStyle>
                  <div>
                    <TextStyle variation="subdued" size="small">
                      {i.supplier || "—"}
                    </TextStyle>
                  </div>
                </div>,
                <TextStyle variation="subdued">
                  {i.description || "—"}
                </TextStyle>,
                <TextStyle variation="subdued">{fmtDate(i.date)}</TextStyle>,
                <TextStyle variation="subdued" size="small">
                  {fmtDate(i.periodFrom)} → {fmtDate(i.periodTo)}
                </TextStyle>,
                distributionLozenge(i),
                <MoneyWithVat
                  amount={i.amountExclVat}
                  vat={btwOf(i)}
                  variation="subdued"
                />,
              ],
            }))}
          />
        </SelectTableLayout>
      )}
    </div>
  );
};

const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 24px;
`;

// The grand total under a building's cards, as far in from the card's edge
// as the amounts in the cells above it (Lewis, 2026-10-17: it stood a
// cell's padding further right). A card's own total gets that from
// CollapsibleCard's TotalsDivider; the provisions step's grand total does
// the same.
const GrandTotal = styled.div`
  padding-right: ${CELL_PADDING};
`;

// Divider that attaches the total to the bottom of the cost-lines card.
// The grand total of the selected costs. Rendered in a separate totals card
// below the cost lines, with the amount in the same style as the lines above.
const SelectedTotal: React.FC<{
  invoices: Invoice[];
  draft: StepContext["draft"];
}> = ({ invoices, draft }) => {
  const chosen = invoices.filter(i => draft.selectedInvoiceIds[i.id]);
  const totalExcl = chosen.reduce((s, i) => s + i.amountExclVat, 0);
  const totalBtw = chosen.reduce((s, i) => s + btwOf(i), 0);
  return (
    <TotalRow>
      <div>
        <TextStyle variation="strong">
          {getLocalizedText("settlement.invoices.total_selected")}
        </TextStyle>
        <div>
          <TextStyle variation="subdued" size="small">
            {getLocalizedText(
              chosen.length === 1
                ? "settlement.invoices.selected_count_one"
                : "settlement.invoices.selected_count_many",
              { count: String(chosen.length) },
            )}
          </TextStyle>
        </div>
      </div>
      {/* Strong like its label, as the Provisies step's total. */}
      <div style={{ textAlign: "right" }}>
        <Money amount={totalExcl} variation="strong" />
        <VatLine vat={totalBtw} />
      </div>
    </TotalRow>
  );
};

// Open tenant costs of this period that the user leaves out: the tenant would
// first get provision back and then pay the left-out costs in full (see
// getOpenCostsWarning). Said up front, so the broker can still take them
// along, also when no cost is selected at all.
const OpenCostsWarning: React.FC<{ invoices: Invoice[]; ctx: StepContext }> = ({
  invoices,
  ctx,
}) => {
  const { draft, settledInvoiceIds, groupOnlyInvoiceIds } = ctx;
  const warning = getOpenCostsWarning({
    invoices,
    selectedInvoiceIds: draft.selectedInvoiceIds,
    excludedInvoiceIds: new Set([
      ...settledInvoiceIds,
      ...(groupOnlyInvoiceIds ?? []),
    ]),
    period: draft.period,
  });
  if (!warning) return null;
  return (
    <Banner variation="warning" hasDismiss={false}>
      {getLocalizedText(warning.textKey, {
        count: String(warning.count),
        from: fmtDate(draft.period.from),
        to: fmtDate(draft.period.to),
      })}
    </Banner>
  );
};

// The late costs of a period already settled (decision 11): see getLateCosts.
const lateCostsOf = (ctx: StepContext, invoices: Invoice[]) =>
  ctx.departureContractId
    ? { invoices: [], settlementNames: [] }
    : getLateCosts({
        invoices,
        excludedInvoiceIds: new Set([
          ...ctx.settledInvoiceIds,
          ...(ctx.groupOnlyInvoiceIds ?? []),
        ]),
        period: ctx.draft.period,
        settledPeriods: ctx.settledPeriods ?? [],
      });

// Costs of a period an earlier settlement already settled that came in
// later (decision 11, 2026-10-20): offered here, with one click to take
// them all along. They are split over their own period, with no provision
// against them.
const LateCostsOffer: React.FC<{ invoices: Invoice[]; ctx: StepContext }> = ({
  invoices,
  ctx,
}) => {
  const { draft, saveDraft } = ctx;
  const late = lateCostsOf(ctx, invoices);
  if (late.invoices.length === 0) return null;
  const open = late.invoices.filter(i => !draft.selectedInvoiceIds[i.id]);
  return (
    <Banner variation="info" hasDismiss={false}>
      <Stack vertical spacing="tight">
        <span>
          {getLocalizedText(
            late.invoices.length === 1
              ? "settlement.invoices.late_costs_one"
              : "settlement.invoices.late_costs_many",
            {
              count: String(late.invoices.length),
              settlements: late.settlementNames.join(", "),
            },
          )}
        </span>
        {open.length > 0 && (
          <div>
            <Button
              appearance="plain"
              onClick={() =>
                saveDraft({
                  selectedInvoiceIds: {
                    ...draft.selectedInvoiceIds,
                    ...Object.fromEntries(open.map(i => [i.id, true])),
                  },
                  ...(draft.noCostsConfirmed
                    ? { noCostsConfirmed: false }
                    : {}),
                })
              }
            >
              {getLocalizedText("settlement.invoices.late_costs_take")}
            </Button>
          </div>
        )}
      </Stack>
    </Banner>
  );
};

// A departure from a unit in a building, before any building cost of its
// period (or of one of its years) is in (decision 4, 2026-10-20, M13): the
// common provision goes back in full and those costs follow later, so the
// deposit may better wait.
const NoCommonCostYetWarning: React.FC<{ ctx: StepContext }> = ({ ctx }) => {
  const { draft, invoices, assignable, settlement, contracts } = ctx;
  if (!ctx.departureContractId) return null;
  const unit = assignable.find(p => String(p.id) === String(settlement.unitId));
  if (!unit?.parentId) return null;
  const lease = contracts.find(c => c.id === ctx.departureContractId);
  const missing = yearsWithoutCommonCost({
    invoices,
    period: draft.period,
    isCommonFixed: !!lease?.fixedFeeCommon,
  });
  if (missing.length === 0) return null;
  const periodYears =
    Number(draft.period.to.slice(0, 4)) -
    Number(draft.period.from.slice(0, 4)) +
    1;
  const tenant = lease?.tenantName ?? contracts[0]?.tenantName ?? "";
  return (
    <Banner variation="warning" hasDismiss={false}>
      {missing.length === periodYears
        ? getLocalizedText("settlement.departure.no_common_cost_yet", {
            tenant,
          })
        : getLocalizedText("settlement.departure.no_common_cost_yet_years", {
            tenant,
            years: missing.join(", "),
          })}
    </Banner>
  );
};

// Costs that run past the period (decision 1, 2026-10-20): offered
// unticked. Taken along, such a cost counts whole, split over its own
// period; a tenant who moves in only after the period pays none of it.
const CostsPastPeriodWarning: React.FC<{
  invoices: Invoice[];
  ctx: StepContext;
}> = ({ invoices, ctx }) => {
  const { draft, settledInvoiceIds, groupOnlyInvoiceIds } = ctx;
  const past = getCostsPastPeriod({
    invoices,
    excludedInvoiceIds: new Set([
      ...settledInvoiceIds,
      ...(groupOnlyInvoiceIds ?? []),
    ]),
    period: draft.period,
    isDeparture: !!ctx.departureContractId,
    selectedInvoiceIds: draft.selectedInvoiceIds,
  });
  if (past.length === 0) return null;
  return (
    <Banner variation="warning" hasDismiss={false}>
      {getLocalizedText(
        past.length === 1
          ? "settlement.invoices.past_period_warning_one"
          : "settlement.invoices.past_period_warning_many",
        {
          count: String(past.length),
          from: fmtDate(draft.period.from),
          to: fmtDate(draft.period.to),
        },
      )}
    </Banner>
  );
};

// With no cost chosen the broker confirms that the period had none (Lewis,
// 2026-10-17): only then does the step go on, and every provision then
// comes back to the tenants. An empty choice used to pass by itself.
const NO_COSTS_OPTION = "no-costs";

const NoCostsConfirmation: React.FC<{ ctx: StepContext }> = ({ ctx }) => {
  const { draft, invoices, saveDraft, settledInvoiceIds } = ctx;
  const chosen = chosenCosts({
    invoices,
    selectedInvoiceIds: draft.selectedInvoiceIds,
    excludedInvoiceIds: settledInvoiceIds,
  });
  if (chosen.length > 0) return null;
  // A departure of a lease settled up to its last day: its costs were all
  // settled, not absent, so no "Er zijn geen kosten" to tick (Y06).
  if (ctx.departureSettled?.isFull) {
    return (
      <Hint>
        {getLocalizedText("settlement.invoices.all_settled_departure", {
          // Up to the lease's end: the days after it are corrected in the
          // next settlement (M07).
          date: fmtDate(
            ctx.departureSettled.pastEnd ?? ctx.departureSettled.until,
          ),
        })}
      </Hint>
    );
  }
  return (
    <Card>
      <OptionListShared
        id="settlement-no-costs"
        multiple
        value={draft.noCostsConfirmed ? [NO_COSTS_OPTION] : []}
        options={[
          {
            id: NO_COSTS_OPTION,
            value: NO_COSTS_OPTION,
            label: getNoCostsText({
              on: "choice",
              departureTenant: ctx.departureContractId
                ? ctx.contracts.find(c => c.id === ctx.departureContractId)
                    ?.tenantName
                : undefined,
            }),
          },
        ]}
        onChange={(values: string[]) =>
          saveDraft({ noCostsConfirmed: values.includes(NO_COSTS_OPTION) })
        }
      />
    </Card>
  );
};

// Why a departure settlement leaves those costs out, once for their
// categories, and how the broker can still take one along.
const YearlyReadingsNote: React.FC<{
  invoices: Invoice[];
  ctx: StepContext;
}> = ({ invoices, ctx }) => {
  const categories = [
    ...new Set(invoices.filter(awaitsReadingsIn(ctx)).map(i => i.category)),
  ];
  if (categories.length === 0) return null;
  return (
    <Banner
      variation="info"
      hasDismiss={false}
      title={categories
        .map(category => CATEGORY_LABELS_NL[category])
        .join(", ")}
    >
      {getLocalizedText("settlement.departure.yearly_readings_info")}
    </Banner>
  );
};

// Costs the settlement cannot take, listed read-only with the reason. Without
// this card they were filtered away entirely: the broker saw a cost in the
// pand's cost list, opened a settlement, and it was simply not there.
const IneligibleCard: React.FC<{ invoices: Invoice[] }> = ({ invoices }) => {
  if (invoices.length === 0) return null;
  return (
    <CollapsibleCard
      title={getLocalizedText("settlement.invoices.ineligible_card", {
        count: String(invoices.length),
      })}
      defaultCollapsed
    >
      <DataTable
        columnContentTypes={["text", "text", "text", "numeric"]}
        headings={[
          getLocalizedText("costs.field.cost"),
          getLocalizedText("costs.field.period"),
          getLocalizedText("costs.field.reason"),
          getLocalizedText("costs.field.amount_excl_vat"),
        ]}
        rows={invoices.map(i => ({
          id: i.id,
          content: [
            <div>
              <TextStyle variation="subdued">
                {CATEGORY_LABELS_NL[i.category]}
              </TextStyle>
              <div>
                <TextStyle variation="subdued" size="small">
                  {i.supplier || "—"}
                </TextStyle>
              </div>
            </div>,
            <TextStyle variation="subdued">
              {fmtDate(i.periodFrom)} → {fmtDate(i.periodTo)}
            </TextStyle>,
            <TextStyle variation="subdued">
              {settlementIneligibleReason(i) ?? "—"}
            </TextStyle>,
            <Money amount={i.amountExclVat} variation="subdued" />,
          ],
        }))}
      />
    </CollapsibleCard>
  );
};

export const StepInvoices: React.FC<StepContext> = ctx => {
  const { settlement, draft, invoices, assignable } = ctx;

  // An empty list here read as "no costs" and let the settlement refund
  // every provision: say that the costs did not load, and nothing else.
  if (ctx.loadFailed) {
    return (
      <div>
        <StepHeader>{getLocalizedText("settlement.invoices.title")}</StepHeader>
        <LoadFailedBanner
          title={getLocalizedText("settlement.load_failed_title")}
          info={getLocalizedText("costs.overview.load_failed_body")}
          onRetry={ctx.onRetryLoad}
        />
      </div>
    );
  }

  const eligible = invoices.filter(settlementEligible);
  const ineligible = invoices.filter(i => !settlementEligible(i));

  if (settlement.scope === "unit") {
    const unitProperty = assignable.find(
      p => String(p.id) === String(settlement.unitId),
    );
    const unitTitle = unitProperty
      ? unitAddressLabel(unitProperty)
      : getLocalizedText("settlement.invoices.costs_of_property");
    const intro = getUnitInvoicesIntro(
      ctx.departureContractId
        ? ctx.contracts.find(c => c.id === ctx.departureContractId)
        : undefined,
    );
    return (
      <div>
        <StepHeader>{getLocalizedText("settlement.invoices.title")}</StepHeader>
        <IntroCopy>
          <TextStyle variation="subdued">
            {getLocalizedText(intro.key, {
              tenant: intro.tenant ?? "",
              until: intro.until ? fmtDate(intro.until) : "",
            })}
          </TextStyle>
        </IntroCopy>
        <Stack vertical spacing="loose">
          <CollapsibleCard
            title={unitTitle}
            totals={<SelectedTotal invoices={eligible} draft={draft} />}
          >
            <InvoiceSelectTable invoices={eligible} ctx={ctx} />
          </CollapsibleCard>
          <YearlyReadingsNote invoices={eligible} ctx={ctx} />
          <NoCommonCostYetWarning ctx={ctx} />
          <LateCostsOffer invoices={eligible} ctx={ctx} />
          <CostsPastPeriodWarning invoices={eligible} ctx={ctx} />
          <OpenCostsWarning invoices={eligible} ctx={ctx} />
          <IneligibleCard invoices={ineligible} />
          <NoCostsConfirmation ctx={ctx} />
        </Stack>
      </div>
    );
  }

  // A building settlement takes everything (2026-10-16): the building's own
  // costs and every unit's own costs, one card per address ("Gebouw:
  // Scenariolaan 1, 9000 Gent (n)", then "Scenariolaan 1 bus 1, 9000 Gent
  // (n)", …, in the order of their box numbers), each with its own
  // select-all and subtotal. The distribution step decides how the
  // building's costs spread over the units; a unit's own costs go to that
  // unit's tenants by their days.
  const propertyOf = (id?: string) =>
    assignable.find(p => String(p.id) === String(id));
  const sections = costSections({
    invoices: eligible,
    unitLabelOf: unitId => {
      const unit = propertyOf(unitId);
      return unit ? unitAddressLabel(unit) : unitId;
    },
    unitSortLabelOf: unitId => {
      const unit = propertyOf(unitId);
      return unit ? unitBoxLabel(unit) : unitId;
    },
    buildingTitle: buildingSectionTitle(
      buildingAddressOf(propertyOf(settlement.groupId)),
    ),
  });
  return (
    <div>
      <StepHeader>{getLocalizedText("settlement.invoices.title")}</StepHeader>
      <IntroCopy>
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.invoices.group_intro")}
        </TextStyle>
      </IntroCopy>

      <Stack vertical spacing="loose">
        {sections.map(section => (
          <CollapsibleCard
            key={section.key}
            title={getLocalizedText("settlement.invoices.section_card", {
              label: section.label,
              count: String(section.invoices.length),
            })}
            totals={<SelectedTotal invoices={section.invoices} draft={draft} />}
          >
            <InvoiceSelectTable invoices={section.invoices} ctx={ctx} />
          </CollapsibleCard>
        ))}

        <LateCostsOffer invoices={eligible} ctx={ctx} />

        <CostsPastPeriodWarning invoices={eligible} ctx={ctx} />

        <OpenCostsWarning invoices={eligible} ctx={ctx} />

        <IneligibleCard invoices={ineligible} />

        {eligible.length === 0 && (
          <Hint>{getLocalizedText("settlement.invoices.none_for_group")}</Hint>
        )}

        <Card>
          <GrandTotal>
            <SelectedTotal invoices={eligible} draft={draft} />
          </GrandTotal>
        </Card>

        <NoCostsConfirmation ctx={ctx} />
      </Stack>
    </div>
  );
};
